/**
 * Loading Progress Manager
 * Provides detailed progress feedback during case loading operations
 */

class LoadingProgress {
  constructor() {
    this.overlay = document.getElementById('loadingOverlay');
    this.message = document.getElementById('loadingMessage');
    this.progressContainer = document.getElementById('loadingProgress');
    this.progressFill = document.getElementById('progressFill');
    this.details = document.getElementById('loadingDetails');

    this.isVisible = false;
    this.currentProgress = 0;
    this.stages = [];
    this.currentStage = 0;
  }

  /**
   * Show the loading overlay with an initial message
   * @param {string} message - Initial loading message
   */
  show(message = 'Loading data...') {
    if (!this.overlay) return;

    this.overlay.style.display = 'flex';
    this.message.textContent = message;
    this.progressContainer.style.display = 'none';
    this.currentProgress = 0;
    this.progressFill.style.width = '0%';
    this.details.style.opacity = '1'; // Ensure initial opacity is correct
    this.isVisible = true;
  }

  /**
   * Hide the loading overlay
   */
  hide() {
    if (!this.overlay) return;

    this.overlay.style.display = 'none';
    this.isVisible = false;
    this.stages = [];
    this.currentStage = 0;
    this.currentProgress = 0;
  }

  /**
   * Initialize progress tracking with defined stages
   * @param {Array<string>} stages - Array of stage descriptions
   */
  initStages(stages) {
    this.stages = stages;
    this.currentStage = 0;
    this.progressContainer.style.display = 'block';
    this.updateProgress(0, stages[0] || 'Initializing...');
  }

  /**
   * Update progress percentage and details
   * @param {number} percent - Progress percentage (0-100)
   * @param {string} detail - Detailed message about current operation
   */
  updateProgress(percent, detail = '') {
    if (!this.isVisible) return;

    this.currentProgress = Math.min(100, Math.max(0, percent));
    this.progressFill.style.width = `${this.currentProgress}%`;

    if (detail && this.details.textContent !== detail) {
      // Smooth text transition to prevent blink
      this.details.style.opacity = '0';
      setTimeout(() => {
        this.details.textContent = detail;
        this.details.style.opacity = '1';
      }, 75);
    }
  }

  /**
   * Move to the next stage
   * @param {string} detail - Optional detail message for the new stage
   */
  nextStage(detail = '') {
    this.currentStage++;

    if (this.currentStage < this.stages.length) {
      const stageMessage = this.stages[this.currentStage];
      this.message.textContent = stageMessage;

      // Auto-calculate progress based on stages
      const progress = (this.currentStage / this.stages.length) * 100;
      this.updateProgress(progress, detail);
    }
  }

  /**
   * Complete the current stage and move to next
   * @param {string} detail - Optional detail message
   */
  completeStage(detail = '') {
    // Set current stage to 100% before moving to next
    const stageProgress = ((this.currentStage + 1) / this.stages.length) * 100;
    this.updateProgress(stageProgress, detail);

    setTimeout(() => {
      this.nextStage(detail);
    }, 100);
  }

  /**
   * Update message without changing progress
   * @param {string} message - New message
   */
  updateMessage(message) {
    if (!this.isVisible) return;
    this.message.textContent = message;
  }

  /**
   * Set a custom detail message
   * @param {string} detail - Detail message
   */
  setDetail(detail) {
    if (!this.isVisible) return;

    if (this.details.textContent !== detail) {
      // Smooth text transition to prevent blink
      this.details.style.opacity = '0';
      setTimeout(() => {
        this.details.textContent = detail;
        this.details.style.opacity = '1';
      }, 75);
    }
  }

  /**
   * Mark loading as complete
   * @param {string} message - Completion message
   */
  complete(message = 'Loading complete!') {
    this.updateProgress(100, message);

    setTimeout(() => {
      this.hide();
    }, 500);
  }

  /**
   * Show error state
   * @param {string} message - Error message
   */
  error(message) {
    if (!this.isVisible) return;

    this.message.textContent = 'Error loading data';
    this.details.textContent = message;
    this.progressFill.style.background = 'var(--error-color)';

    setTimeout(() => {
      this.hide();
      this.progressFill.style.background = 'linear-gradient(90deg, var(--apple-blue), var(--apple-accent))';
    }, 3000);
  }
}

// Create global instance
window.loadingProgress = new LoadingProgress();
